import { useEffect, useState } from "react";
import { Container, Spinner, Card, Row, Col, Button } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { usePage } from "../../hooks/usePage";
import { usePrecargaEvents } from "../../hooks/usePrecargaEvents";
import { legacyAdminGet } from "../../utils/PrecargaApi";
import FastTable from "../../components/FastTable/FastTable";

export default function PrecargasWalletDetail() {
  usePage({ title: "Precargas - Detalle de Billetera" });

  const { eventid, walletId } = useParams();
  const navigate = useNavigate();
  const cashlessEventId = eventid ? parseInt(eventid) : null;

  const { precargaEvent, isLoadingPrecargaEvent } = usePrecargaEvents({
    cashlessEventId,
  });

  const [detail, setDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (precargaEvent?.id && walletId) {
      setIsLoading(true);
      legacyAdminGet(`${precargaEvent.id}/wallets/${walletId}/detail`)
        .then((data) => {
          setDetail(data);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching wallet detail:", error);
          setIsLoading(false);
        });
    }
  }, [precargaEvent?.id, walletId]);

  if (isLoadingPrecargaEvent || isLoading) {
    return (
      <Container fluid>
        <div className="text-center p-5">
          <Spinner animation="border" />
          <p className="mt-3">Cargando detalle de billetera...</p>
        </div>
      </Container>
    );
  }

  if (!detail) {
    return (
      <Container fluid>
        <div className="text-center p-5">
          <p>No se encontro la billetera</p>
          <Button onClick={() => navigate(`/${eventid}/preloads/wallets`)}>
            Volver a Billeteras
          </Button>
        </div>
      </Container>
    );
  }

  // Supports both response shapes:
  // 1) { wallet, user, orders, cardLinks, totalSpent, ordersCount, linksCount }
  // 2) legacy single object with wallet fields and nested arrays
  const walletData = detail.wallet || detail;
  const userData = detail.user || walletData?.user || null;
  const orders = detail.orders || walletData?.orders || [];
  const cardLinks = detail.cardLinks || walletData?.cardLinks || [];

  const ordersCount =
    detail.ordersCount ??
    detail.total_orders ??
    (Array.isArray(orders) ? orders.length : 0);

  const linksCount =
    detail.linksCount ??
    detail.card_links_count ??
    (Array.isArray(cardLinks) ? cardLinks.length : 0);

  const totalSpent = detail.totalSpent ?? detail.total_spent ?? 0;

  const formatMoney = (value) => {
    if (value === null || value === undefined) return "N/A";
    return `$${value}`;
  };

  const formatDate = (value) => {
    if (!value) return "N/A";
    return new Date(value).toLocaleString();
  };

  const ordersColumns = [
    {
      label: "ID",
      key: "id",
      type: "number",
      width: 80,
    },
    {
      label: "Order ID",
      key: "orderId",
      type: "string",
    },
    {
      label: "Monto",
      key: "amount",
      type: "currency",
      width: 120,
    },
    {
      label: "Estado",
      key: "status",
      type: "string",
      width: 120,
    },
    {
      label: "Fecha",
      key: "createdAt",
      type: "date",
      width: 180,
    },
  ];

  const cardLinksColumns = [
    {
      label: "ID",
      key: "id",
      type: "number",
      width: 80,
    },
    {
      label: "Link ID",
      key: "linkId",
      type: "string",
      render: (row) => row.linkId || "N/A",
    },
    {
      label: "Estado",
      key: "status",
      type: "string",
      width: 120,
    },
    {
      label: "Fecha",
      key: "createdAt",
      type: "date",
      width: 180,
    },
  ];

  return (
    <Container fluid>
      <Row className="mb-3">
        <Col>
          <Button
            variant="secondary"
            onClick={() => navigate(`/${eventid}/preloads/wallets`)}
          >
            {"<- Volver a Billeteras"}
          </Button>
        </Col>
      </Row>

      <h2>Detalle de Billetera</h2>

      <Row>
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>
              <strong>Informacion de la Billetera</strong>
            </Card.Header>
            <Card.Body>
              <p>
                <strong>ID:</strong> {walletData?.id || "N/A"}
              </p>
              <p>
                <strong>Evento ID:</strong> {walletData?.eventId || "N/A"}
              </p>
              <p>
                <strong>Saldo en Cuenta:</strong>{" "}
                {formatMoney(walletData?.balanceAccount)}
              </p>
              <p>
                <strong>Saldo en Tarjeta:</strong>{" "}
                {formatMoney(walletData?.balanceCard)}
              </p>
              <p>
                <strong>Saldo Total:</strong> {formatMoney(walletData?.totalBalance)}
              </p>
              <p>
                <strong>Estado de Tarjeta:</strong> {walletData?.cardStatus || "N/A"}
              </p>
              <p>
                <strong>Reconciliando:</strong> {walletData?.reconciling ? "Si" : "No"}
              </p>
              <p>
                <strong>Elegible para Reembolso:</strong>{" "}
                {walletData?.refundEligible ? "Si" : "No"}
              </p>
              <p>
                <strong>Fecha de Creacion:</strong> {formatDate(walletData?.createdAt)}
              </p>
              <p>
                <strong>Ultima Actualizacion:</strong> {formatDate(walletData?.updatedAt)}
              </p>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>
              <strong>Informacion del Usuario</strong>
            </Card.Header>
            <Card.Body>
              <p>
                <strong>ID:</strong> {userData?.id || "N/A"}
              </p>
              <p>
                <strong>Email:</strong> {userData?.email || "N/A"}
              </p>
              <p>
                <strong>Telefono:</strong> {userData?.phone || "N/A"}
              </p>
              <p>
                <strong>Email Verificado:</strong>{" "}
                {userData?.emailVerified ? "Si" : "No"}
              </p>
              <p>
                <strong>Telefono Verificado:</strong>{" "}
                {userData?.phoneVerified ? "Si" : "No"}
              </p>
              {userData?.id && (
                <Button
                  variant="link"
                  onClick={() =>
                    navigate(`/${eventid}/preloads/participants/${userData.id}`)
                  }
                >
                  Ver Participante
                </Button>
              )}
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header>
              <strong>Estadisticas</strong>
            </Card.Header>
            <Card.Body>
              <p>
                <strong>Total de Ordenes:</strong> {ordersCount}
              </p>
              <p>
                <strong>Total Gastado:</strong> {formatMoney(totalSpent)}
              </p>
              <p>
                <strong>Vinculaciones de Tarjeta:</strong> {linksCount}
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {orders.length > 0 && (
        <Row className="mt-4">
          <Col>
            <h4>Ordenes ({orders.length})</h4>
            <FastTable
              title="Ordenes"
              data={orders}
              columns={ordersColumns}
              isLoading={false}
              isError={false}
              showExcel={false}
              showFilters={false}
              showPagination={false}
              onSelectRow={(row) =>
                navigate(`/${eventid}/preloads/orders/${row.id}`)
              }
            />
          </Col>
        </Row>
      )}

      {cardLinks.length > 0 && (
        <Row className="mt-4">
          <Col>
            <h4>Vinculaciones de Tarjetas ({cardLinks.length})</h4>
            <FastTable
              title="Vinculaciones"
              data={cardLinks}
              columns={cardLinksColumns}
              isLoading={false}
              isError={false}
              showExcel={false}
              showFilters={false}
              showPagination={false}
              onSelectRow={(row) =>
                navigate(`/${eventid}/preloads/card-links/${row.linkId || row.id}`)
              }
            />
          </Col>
        </Row>
      )}
    </Container>
  );
}
